/**
 * The one editor for a list of opening intervals.
 *
 * Shared by the weekly schedule and by a MODIFIED_HOURS closure, which had been
 * carrying two copies of the same forty lines.
 *
 * `withDerivedNextDay` is the rule — 07:00 → 01:00 can only mean the following
 * morning, so the editor settles the flag rather than making someone find a
 * checkbox. What is left here is how that reads: the row says in words which
 * day it closes on, and the checkbox survives for the one pair of times that
 * still needs an answer.
 */

import type { HoursInterval } from '@diora/contracts';
import { formatClock, withDerivedNextDay } from '@diora/contracts';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Input } from '@/components/ui';
import { useLocale } from '@/providers/locale';

export function IntervalRows({
  intervals,
  onChange,
  disabled,
  removable = false,
}: {
  intervals: HoursInterval[];
  onChange: (next: HoursInterval[]) => void;
  disabled?: boolean;
  /** The weekly editor can drop an interval; a closure's list cannot. */
  removable?: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  function patch(index: number, change: Partial<HoursInterval>) {
    onChange(
      intervals.map((interval, position) =>
        position === index ? withDerivedNextDay({ ...interval, ...change }) : interval,
      ),
    );
  }

  return (
    <>
      {intervals.map((interval, index) => {
        // The times are equal, so nothing but the checkbox can say whether this
        // is twenty-four hours or nothing at all.
        const ambiguous = interval.opensAt === interval.closesAt;

        return (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <Input
              type="time"
              dir="ltr"
              className="tabular"
              aria-label={t('hours.opensAt')}
              disabled={disabled}
              value={interval.opensAt}
              onChange={(event) => patch(index, { opensAt: event.target.value })}
            />
            <ArrowRight className="dio-icon dio-icon--directional" aria-hidden="true" />
            <Input
              type="time"
              dir="ltr"
              className="tabular"
              aria-label={t('hours.closesAt')}
              disabled={disabled}
              value={interval.closesAt}
              onChange={(event) => patch(index, { closesAt: event.target.value })}
            />

            {ambiguous ? (
              <Checkbox
                checked={interval.closesNextDay}
                disabled={disabled}
                label={t('hours.closesNextDay')}
                onChange={(checked) => patch(index, { closesNextDay: checked })}
              />
            ) : (
              interval.closesNextDay && (
                // Says what was understood, rather than leaving the editor to
                // infer it from a control they did not touch.
                <span className="dio-hint">
                  {t('hours.closesNextDayNote', { time: formatClock(interval.closesAt, locale) })}
                </span>
              )
            )}

            {removable && !disabled && (
              <Button
                size="sm"
                iconOnly
                variant="ghost"
                aria-label={t('hours.removeInterval')}
                onClick={() => onChange(intervals.filter((_, position) => position !== index))}
              >
                <Trash2 className="dio-icon" aria-hidden="true" />
              </Button>
            )}
          </div>
        );
      })}

      {!disabled && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onChange([...intervals, { opensAt: '18:00', closesAt: '23:00', closesNextDay: false }])}
        >
          <Plus className="dio-icon" aria-hidden="true" />
          {t('hours.addInterval')}
        </Button>
      )}
    </>
  );
}
